/* eslint-disable react/no-unstable-nested-components */
import { router, SplashScreen, Tabs, usePathname } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import { Platform, TouchableOpacity } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import { useAdmin } from '@/api/partners/admins/use-admin';
import { useModalStore } from '@/components/login-modal/store';
import { isDesktop, useAuth } from '@/core';
import useSearchStore from '@/stores/use-search-store';
import { GREY_40 } from '@/style/colors';
import { Icon } from '@/ui/icon';
import { Car } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Dashboard } from '@/ui/icons/dashboard';
import { Dollar } from '@/ui/icons/dollar';
import { Dots } from '@/ui/icons/dots';
import { formatCountBadge } from '@/ui/menu-item';

export default function PartnerTabLayout() {
  const { theme } = useStyles();
  const status = useAuth.use.status();
  const pathname = usePathname();
  const hideSplash = useCallback(async () => {
    await SplashScreen.hideAsync();
  }, []);
  const viewMode = useSearchStore((state) => state.viewMode);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const loginModalMode = useModalStore((state) => state.loginModalMode);
  const { data: admin } = useAdmin();
  const moreTabBadge = formatCountBadge(admin?.unreadChangelogCount);

  useEffect(() => {
    if (status !== 'idle') {
      setTimeout(() => {
        hideSplash();
      }, 1000);
    }
  }, [hideSplash, status]);

  useEffect(() => {
    if (
      status === 'signOut' &&
      loginModalMode === 'none' &&
      !['/', '/account/support-faq'].includes(pathname) &&
      !pathname.includes('/login')
    ) {
      // Defer to the next tick so the root navigator has finished mounting,
      // otherwise a cold deep-link load throws "Attempted to navigate before
      // mounting the Root Layout component."
      const timer = setTimeout(() => {
        router.replace(
          `/partner/login?returnTo=${encodeURIComponent(pathname)}`
        );
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [status, pathname, loginModalMode]);

  useEffect(() => {
    if (
      status === 'signOut' &&
      ['/', '/account/support-faq', '/subscriptions'].includes(pathname)
    ) {
      setLoginModalMode('none');
    }
  }, [status, pathname]);

  return (
    <Tabs
      screenOptions={({ route }) => ({
        sceneStyle: {
          backgroundColor: theme.colors.background_1,
        },
        tabBarLabelStyle: {
          fontFamily: 'DMSans-Regular',
          includeFontPadding: false,
        },
        tabBarStyle: {
          backgroundColor: theme.colors.background_2,
          borderTopColor: theme.colors.card.default.borderColor,
          borderColor: theme.colors.card.default.borderColor,
          borderTopWidth: 1,
          height: 60 + UnistylesRuntime.insets.bottom,
          paddingBottom: UnistylesRuntime.insets.bottom,
          ...(pathname === '/' &&
            viewMode === 'map' && {
              height: 0,
              display: 'none',
            }),
          ...(isDesktop() && {
            height: 0,
            display: 'none',
          }),
        },
        headerStyle: {
          backgroundColor: theme.colors.background_1,
        },
        headerTintColor: theme.colors.text.primary,
        headerShadowVisible: false,
        tabBarActiveTintColor: theme.colors.text.primary,
        tabBarInactiveTintColor: GREY_40,
        tabBarIcon: ({ focused }) => {
          if (route.name === 'dashboard') {
            return (
              <Icon
                type="naked"
                icon={<Dashboard />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'cars') {
            return (
              <Icon
                type="naked"
                icon={<Car />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'reservations') {
            return (
              <Icon
                type="naked"
                icon={<CalendarIcon />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'payouts') {
            return (
              <Icon
                type="naked"
                icon={<Dollar />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'more') {
            return (
              <Icon
                type="naked"
                icon={<Dots />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          }
        },
      })}
    >
      <Tabs.Screen
        name="dashboard"
        options={{
          title: 'Dashboard',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/partner/dashboard');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="cars"
        options={{
          title: 'Cars',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/partner/cars');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="reservations"
        options={{
          title: 'Reservations',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/partner/reservations');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="payouts"
        options={{
          title: 'Payouts',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/partner/payouts');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="more"
        options={{
          title: 'More',
          headerShown: false,
          tabBarBadge: moreTabBadge,
          tabBarBadgeStyle: {
            backgroundColor: theme.colors.button.primary.default.fill,
            color: theme.colors.button.primary.default.text,
            top: -10,
            end: -8,
          },
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/partner/more');
                }}
              />
            );
          },
        }}
      />
    </Tabs>
  );
}
